import * as React from 'react';

import {react2panel} from 'common/util/react2panel';
import {withStyles} from 'common/util/react';
import {
  EmbedReplayParams,
  EmbedReplayPayload,
  LoadEmbedParams,
  ParentAppReplayMessage,
  RecordingEvents,
  RecordingMetadata,
  RecordingPlayerSource,
  RecordingPreviewMetadata,
  RecordingSpeed,
  WindowMessageRecipient,
} from 'common/widgets/recording-player/types';
import {useTranslation} from 'common/i18n/hooks';
import {getMaximumSRRetentionPeriod, sessionRecordingQueryUtils} from 'common/widgets/recording-player/query-utils';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {reporter} from 'common/report/error-reporting';
import {getSessionRecordingBlockingInfo} from 'common/report/util/feature-sampling';
import {
  EVENT_TIME_JUMP_OFFSET,
  PLAYER_URL,
  REPORT_EVENT_TIME_JUMP_OFFSET,
} from 'common/widgets/recording-player/player';
import {SpinnerReact} from 'common/components/spinner';
import {ReactErrorBoundary} from 'common/components/react-error-boundary';
import {useLocalStorageManager} from 'common/contexts/react/storage-manager-context';
import {useThemes} from 'common/contexts/react/themes-context';
import {TooltipReact} from 'common/components/tooltip';
import {useReplayPreviewCache} from 'common/contexts/react/replay-preview-cache-context';

import {RecordingPlayerModalAsync} from '../recording-player';
import {
  fetchSignedReplayParams,
  sendEmbedReplayMessage,
  useReplayWindowMessageListener,
} from '../recording-player/util';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
import {PreviewMetadata, ReplayPreviewProps, Status} from './types';
import {NoDataState} from './no-data-state';
import {track, TrackingEvent, TrackingProperties} from './tracking';

const REPLAY_FETCH_DELAY = 500; // wait 500ms on first hover before querying
const RESULTS_LIMIT = 30;
const REPLAY_PREVIEW_ERROR_PREFIX = `[Replay Preview]`;

export const ReplayPreview = ({eventName}: ReplayPreviewProps) => {
  const [previewMetadata, setPreviewMetadata] = React.useState<PreviewMetadata | null>(null);
  const [embedReady, setEmbedReady] = React.useState<boolean>(false);
  const [embedStartTimestamp, setEmbedStartTimestamp] = React.useState<null | number>(null);
  const [embedPlaying, setEmbedPlaying] = React.useState<boolean>(false);
  const [trackingProperties, setTrackingProperties] = React.useState<TrackingProperties>({});
  const [status, setStatus] = React.useState<Status>(`loading`);
  const [errorMessage, setErrorMessage] = React.useState<string>(``);
  const embedIframeRef = React.useRef<HTMLIFrameElement>(null);
  const previewCache = useReplayPreviewCache();

  const currentProject = useCurrentProject().get().value;
  const i18n = useTranslation();
  const {t} = i18n;
  const localStorageManager = useLocalStorageManager();
  const isDarkMode = useThemes().isDarkMode();

  const sendEmbedMessage = (payload: EmbedReplayPayload) => {
    if (embedIframeRef.current && embedIframeRef.current.contentWindow) {
      sendEmbedReplayMessage({...payload, isDarkMode}, embedIframeRef.current.contentWindow);
    }
  };

  const getModalReplaysAndEvents = async (): Promise<{replays: RecordingMetadata[]; events: RecordingEvents[]}> => {
    if (!currentProject) {
      return {replays: [], events: []};
    }
    const {recordings: replays, targetEventMapping: events} =
      await sessionRecordingQueryUtils.fetchRecordingsAndEventsForEventName({
        blockedReplayFrames: getSessionRecordingBlockingInfo(currentProject),
        maxRetentionPeriod: getMaximumSRRetentionPeriod(currentProject),
        i18n,
        eventInfo: eventName,
        currentProject,
        resultsLimit: RESULTS_LIMIT,
      });
    return {replays, events};
  };

  const getPreviewReplayAndEvent = async (): Promise<RecordingPreviewMetadata[]> => {
    if (!currentProject) {
      return [];
    }
    return sessionRecordingQueryUtils.fetchRecordingPreviewMetadataForEventName({
      blockedReplayFrames: getSessionRecordingBlockingInfo(currentProject),
      maxRetentionPeriod: getMaximumSRRetentionPeriod(currentProject),
      eventName,
      currentProject,
      resultsLimit: RESULTS_LIMIT,
    });
  };

  const getReplays = async () => {
    try {
      const startLoadTimestamp = Date.now();
      // Then, get replays info
      const previewsFound = await getPreviewReplayAndEvent();
      if (previewsFound.length) {
        const preview = previewsFound[0];
        const {replayId, eventSelectionTime} = preview;
        const metadata = {
          hasReplay: true,
          replayId,
          startTime: eventSelectionTime,
        };
        previewCache.set(eventName, metadata);
        setPreviewMetadata(metadata);

        const endLoadTimestamp = Date.now();
        // Also, start tracking the start timestamp for load
        setTrackingProperties({
          EventName: eventName,
          FetchTimeMs: endLoadTimestamp - startLoadTimestamp,
          LoadTimeMs: endLoadTimestamp,
        });
      } else {
        previewCache.set(eventName, {hasReplay: false});
        // For no replays, still track how long fetch took, but don't include timestamps
        // for loading (we wont be loading anything). Immediately track it
        track(TrackingEvent.ReplaysPreviewNoReplays, {
          FetchTimeMs: Date.now() - startLoadTimestamp,
        });
      }
      setStatus(`success`);
    } catch (err) {
      // If it is already set to 'error' (after we retry), log out the error. First time don't and let it retry first
      setErrorMessage(err.message);
      setStatus(`error`);
      reporter.error(`${REPLAY_PREVIEW_ERROR_PREFIX}: ${err.message}`);
      track(TrackingEvent.ReplaysPreviewError);
    }
  };

  /* Embedded player logic */
  useReplayWindowMessageListener<ParentAppReplayMessage>(WindowMessageRecipient.ParentApp, window, (message) => {
    const payload = message.payload;
    if (payload.embedReady) {
      setEmbedReady(true);
    }

    if (payload.recordingStartTime && embedStartTimestamp === null) {
      const eventSelectedTime =
        (Math.max((previewMetadata?.startTime || 0) - REPORT_EVENT_TIME_JUMP_OFFSET, 0) - EVENT_TIME_JUMP_OFFSET) *
        1000;
      const timeFromRecordingStart = Math.max(eventSelectedTime - payload.recordingStartTime, 0);
      sendEmbedMessage({
        recordingProgress: timeFromRecordingStart,
        recordingPaused: false,
      });
      setEmbedStartTimestamp(timeFromRecordingStart);
    }

    if (payload.recordingProgress && embedStartTimestamp && payload.recordingProgress < embedStartTimestamp) {
      sendEmbedMessage({
        recordingProgress: embedStartTimestamp,
        recordingPaused: false,
      });
    }

    if (payload.error) {
      // Indicate no replays available, but log out error
      setPreviewMetadata(null);
      reporter.error(`${REPLAY_PREVIEW_ERROR_PREFIX}: ${payload.error}`);
    }
  });

  const startReplayPreview = async () => {
    if (!embedPlaying && embedReady && previewMetadata && previewMetadata.hasReplay && previewMetadata.replayId) {
      const signedParams = await fetchSignedReplayParams(previewMetadata.replayId, `prod`);

      const replayParams: EmbedReplayParams = {
        signedParams,
        replayId: previewMetadata.replayId,
        retentionPeriod: getMaximumSRRetentionPeriod(currentProject),
      };
      const embedParams: LoadEmbedParams = {
        replayParams: [replayParams],
        targetOrigin: window.location.origin,
      };

      sendEmbedMessage({
        embedParams,
        recordingSpeed: RecordingSpeed.Double,
        recordingPaused: false,
      });
      setEmbedPlaying(true);
      setTrackingProperties((oldTimestamps) => {
        const loadTime = Date.now() - (oldTimestamps.LoadTimeMs ?? 0);
        const fetchTime = oldTimestamps.FetchTimeMs ?? 0;
        const properties = {
          EventName: eventName,
          FetchTimeMs: fetchTime,
          LoadTimeMs: loadTime,
          TotalTimeMs: loadTime + fetchTime,
        };
        track(TrackingEvent.ReplaysPreviewLoaded, properties);
        return properties;
      });
    }
  };

  React.useEffect(() => {
    // Reset ALL state
    setStatus(`loading`);
    setPreviewMetadata(null);
    setEmbedReady(false);
    setEmbedPlaying(false);
    setEmbedStartTimestamp(null);
    setTrackingProperties({});
    if (!currentProject || currentProject.hasIntegratedSessionReplay) {
      const metadata = previewCache.get(eventName);

      if (!metadata) {
        // Cache miss, go fetch replays
        const timerId = setTimeout(getReplays, REPLAY_FETCH_DELAY);
        return () => {
          clearTimeout(timerId);
        };
      } else if (!metadata.hasReplay) {
        // Cache hit, no replays for this event
        setStatus(`success`);
      } else {
        // Cache hit, replays found for this event
        setPreviewMetadata(metadata);
        // If cache hit, just say fetch was 0ms
        // Also, start tracking the start timestamp for load
        setTrackingProperties({
          EventName: eventName,
          FetchTimeMs: 0,
          LoadTimeMs: Date.now(),
        });
        // Sometimes we are so fast on event changes that the old content window is still there. If so, then set that
        // the embed/content window is already ready to receive params
        if (embedIframeRef.current && embedIframeRef.current.contentWindow) {
          setEmbedReady(true);
        }
        setStatus(`success`);
      }
    } else {
      // If they don't have it enabled, just say there are no replays
      setStatus(`success`);
    }
    return () => {};
  }, [currentProject, eventName]);

  React.useEffect(() => {
    if (!currentProject || currentProject.hasIntegratedSessionReplay) {
      startReplayPreview();
    }
  }, [embedReady, previewMetadata, eventName]);

  let content;
  if (status === `loading`) {
    content = (
      <div className={styles.spinnerContainer}>
        <SpinnerReact className={styles.spinner} />
      </div>
    );
  } else if (status === `error`) {
    content = <NoDataState isError onRefresh={() => getReplays()} errorMessage={errorMessage} />;
  } else {
    if (!previewMetadata) {
      content = <NoDataState />;
    } else {
      content = (
        <div className={styles.playerIframeContainer}>
          <TooltipReact text={t(`session-replay.preview-hover`)} />
          <div
            className={styles.playerIframeOverlay}
            onClick={() => {
              track(TrackingEvent.ReplaysPreviewClicked, trackingProperties);
              const eventsAndReplaysPromise = getModalReplaysAndEvents();
              return RecordingPlayerModalAsync({
                eventsAndReplaysPromise,
                localStorageManager,
                source: RecordingPlayerSource.QueryPreview,
                startingReplayId: previewMetadata.replayId,
              });
            }}
          >
            {!embedReady && (
              <div className={styles.spinnerContainer}>
                <SpinnerReact className={styles.spinner} />
              </div>
            )}
          </div>
          <iframe className={styles.playerIframe} src={PLAYER_URL} ref={embedIframeRef} />
        </div>
      );
    }
  }

  return (
    <div>
      <ReactErrorBoundary>{content}</ReactErrorBoundary>
    </div>
  );
};

export const ReplayPreviewReact = withStyles(ReplayPreview, css);
export const ReplayPreviewEl = react2panel(`mp-replay-preview`, ReplayPreviewReact);
